import React, { useState } from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import NormalTextField from '../../../common/NormalTextField';
import { SectionHeader } from './UIComponents';
import StorageIcon from '@mui/icons-material/Storage';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NetworkService from '../../../../services/NetworkService';

const DomainManagement = ({ 
  domains, 
  fetchDomains, 
  testDomainConnection,
  showNotification
}) => {
  const [newDomain, setNewDomain] = useState({
    DOMAIN_NAME: '',
    DOMAIN_SERVER: '',
    DOMAIN_PORT: '',
    BASE_DN: '',
    BIND_DN: '',
    PASSWORD: ''
  });
  const [editDomainId, setEditDomainId] = useState(null);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'success', 'error'

  const handleDomainChange = (e) => {
    const { name, value } = e.target;
    setNewDomain({ ...newDomain, [name]: value });
  };

  const addDomain = async (e) => {
    e.preventDefault();
    try {
      if (editDomainId) {
        const response = await NetworkService.updateNetworkDomain(editDomainId, newDomain);
        if (response) {
          showNotification('Successful', 'Successfully updated domain configuration');
          await fetchDomains();
          resetDomainForm();
        }
      } else {
        const response = await NetworkService.createNetworkDomain(newDomain);
        if (response) {
          showNotification('Successful', 'Successfully added new domain configuration');
          await fetchDomains();
          resetDomainForm();
        }
      }
    } catch (error) {
      console.error('Error adding/updating domain:', error);
      showNotification('Error', error.message || 'An error occurred');
    }
  };

  const editDomain = (domain) => {
    setNewDomain({
      DOMAIN_NAME: domain.DOMAIN_NAME,
      DOMAIN_SERVER: domain.DOMAIN_SERVER,
      DOMAIN_PORT: domain.DOMAIN_PORT,
      BASE_DN: domain.BASE_DN,
      BIND_DN: domain.BIND_DN,
      PASSWORD: '' // For security reasons, we don't pre-fill the password
    });
    setEditDomainId(domain.id);
    setShowDomainForm(true);
    setConnectionStatus(null); // Reset connection status when editing
  };

  const deleteDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to delete this domain?')) {
      try {
        await NetworkService.deleteNetworkDomain(domainId);
        showNotification('Successful', 'Successfully deleted domain configuration');
        await fetchDomains();
      } catch (error) {
        console.error('Error deleting domain:', error);
        showNotification('Error', error.message || 'An error occurred');
      }
    }
  };

  const resetDomainForm = () => {
    setNewDomain({
      DOMAIN_NAME: '',
      DOMAIN_SERVER: '',
      DOMAIN_PORT: '',
      BASE_DN: '',
      BIND_DN: '',
      PASSWORD: ''
    });
    setEditDomainId(null);
    setShowDomainForm(false);
    setConnectionStatus(null); // Reset connection status
  };

  const handleTestConnection = async () => {
    // Prevent testing without a password
    if (!newDomain.PASSWORD && !editDomainId) {
      showNotification('Error', 'Please enter a password to test the connection');
      return;
    }
    
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      // Display inline status while waiting
      const result = await testDomainConnection(editDomainId, newDomain.PASSWORD);
      
      if (result.success) {
        setConnectionStatus('success');
        // Also show notification for additional feedback
        showNotification('Connection Successful', `Successfully connected to ${newDomain.DOMAIN_NAME}`);
      } else {
        setConnectionStatus('error');
        showNotification('Connection Failed', result.message || 'Failed to connect to domain');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setConnectionStatus('error');
      showNotification('Connection Error', error.message || 'An unexpected error occurred');
    } finally {
      setTestingConnection(false);
    }
  };

  // Get connection status message and color
  const getConnectionStatusInfo = () => {
    if (connectionStatus === 'success') {
      return { message: 'Connection successful', color: 'success.main' };
    } else if (connectionStatus === 'error') {
      return { message: 'Connection failed', color: 'error.main' };
    }
    return { message: '', color: 'text.secondary' };
  };

  // Domain columns for DataGrid
  const domainColumns = [
    { field: 'DOMAIN_NAME', headerName: 'Domain Name', flex: 1 },
    { field: 'DOMAIN_SERVER', headerName: 'Server', flex: 1 },
    { field: 'DOMAIN_PORT', headerName: 'Port', width: 80 },
    { field: 'BASE_DN', headerName: 'Base DN', flex: 1 },
    { field: 'BIND_DN', headerName: 'Bind DN', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <mui.Stack direction="row" spacing={1}>
          <mui.Button 
            size="small" 
            variant="outlined" 
            color="primary"
            startIcon={<LockOpenIcon fontSize="small" />}
            onClick={() => {
              editDomain(params.row);
              // Don't auto-test - just open the edit form
            }}
          >
            Test
          </mui.Button>
          <mui.IconButton size="small" onClick={() => editDomain(params.row)}>
            <EditIcon fontSize="small" />
          </mui.IconButton>
          <mui.IconButton size="small" onClick={() => deleteDomain(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </mui.IconButton>
        </mui.Stack>
      ),
    },
  ];

  return (
    <mui.Paper sx={{ p: 3, mb: 4 }}>
      <SectionHeader 
        title="Domain Management" 
        icon={<StorageIcon />} 
        description="Add and configure multiple AD domain connections" 
        action={
          <mui.Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setShowDomainForm(true)}
            disabled={showDomainForm}
          >
            Add Domain
          </mui.Button>
        }
      />
      
      {showDomainForm && (
        <mui.Box sx={{ mt: 3, mb: 4, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
          <mui.Typography variant="h6" gutterBottom>
            {editDomainId ? 'Edit Domain' : 'Add New Domain'}
          </mui.Typography>

          <mui.Box component="form" onSubmit={addDomain}>
            <mui.Grid container spacing={3}>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Domain Name"
                  name="DOMAIN_NAME"
                  value={newDomain.DOMAIN_NAME}
                  onChange={handleDomainChange}
                  required={true}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Domain Server"
                  name="DOMAIN_SERVER"
                  value={newDomain.DOMAIN_SERVER}
                  onChange={handleDomainChange}
                  required={true}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Domain Port"
                  name="DOMAIN_PORT"
                  value={newDomain.DOMAIN_PORT}
                  onChange={handleDomainChange}
                  required={true}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Base DN"
                  name="BASE_DN"
                  value={newDomain.BASE_DN}
                  onChange={handleDomainChange}
                  required={true}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Bind DN"
                  name="BIND_DN"
                  value={newDomain.BIND_DN}
                  onChange={handleDomainChange}
                  required={true}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Password"
                  name="PASSWORD"
                  value={newDomain.PASSWORD}
                  onChange={handleDomainChange}
                  type="password"
                  required={!editDomainId}
                  helperText={editDomainId ? "Leave blank to keep current password" : ""}
                />
              </mui.Grid>
              <mui.Grid item xs={12}>
                <mui.Stack direction="row" spacing={2} alignItems="center">
                  <mui.Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    startIcon={editDomainId ? <CloudSyncIcon /> : <AddIcon />}
                  >
                    {editDomainId ? 'Update Domain' : 'Add Domain'}
                  </mui.Button>
                  {editDomainId && (
                    <>
                      <mui.Button 
                        onClick={handleTestConnection} 
                        variant="outlined"
                        startIcon={testingConnection ? <mui.CircularProgress size={16} /> : <LockOpenIcon />}
                        disabled={testingConnection}
                        color={connectionStatus ? (connectionStatus === 'success' ? 'success' : 'error') : 'primary'}
                      >
                        {testingConnection ? 'Testing...' : 'Test Connection'}
                      </mui.Button>
                      {connectionStatus && (
                        <mui.Typography 
                          variant="body2" 
                          color={getConnectionStatusInfo().color}
                          sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}
                        >
                          {connectionStatus === 'success' && <CheckCircleOutlineIcon sx={{ mr: 0.5 }} fontSize="small" />}
                          {connectionStatus === 'error' && <ErrorOutlineIcon sx={{ mr: 0.5 }} fontSize="small" />}
                          {getConnectionStatusInfo().message}
                        </mui.Typography>
                      )}
                    </>
                  )}
                  <mui.Button 
                    onClick={resetDomainForm} 
                    variant="outlined" 
                    color="secondary"
                  >
                    Cancel
                  </mui.Button>
                </mui.Stack>
              </mui.Grid>
            </mui.Grid>
          </mui.Box>
        </mui.Box>
      )}
      
      <mui.Box sx={{ height: 400, width: '100%', mt: showDomainForm ? 0 : 3 }}>
        {domains.length > 0 ? (
          <DataGrid
            rows={domains}
            columns={domainColumns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        ) : (
          <mui.Box sx={{ p: 3, textAlign: 'center' }}>
            <StorageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <mui.Typography variant="body1" gutterBottom>
              No domains configured
            </mui.Typography>
            <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add your first AD domain to start managing network access
            </mui.Typography>
            <mui.Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowDomainForm(true)}
            >
              Add Domain
            </mui.Button>
          </mui.Box>
        )}
      </mui.Box>
    </mui.Paper>
  );
};

export default DomainManagement; 